import type { DeviceMemory } from '#src/groups/device_memory/device_memory.ts';
import { DeviceMemoryRules } from '#src/groups/device_memory/device_memory.ts';
import type { DeviceMemoryStore } from '#src/groups/device_memory/device_memory_store.ts';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

export interface UseDeviceMemoryResult {
  memory: DeviceMemory;
  /** Remembers the confirmed name on this device. */
  select: (participantId: string) => void;
  /**
   * Records that the draw animation showed this participant's match on this device.
   * Ignored when another tab has since selected a different name.
   */
  markRevealed: (participantId: string) => void;
  /** Forgets the confirmed name. Keeps reveal history, the match, and the wishlist. */
  forget: () => void;
}

export function useDeviceMemory(store: DeviceMemoryStore, groupId: string): UseDeviceMemoryResult {
  const getSnapshot = useCallback(() => store.get(groupId), [store, groupId]);
  const memory = useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);

  return useMemo(
    () => ({
      memory,
      select: participantId =>
        store.update(groupId, current => DeviceMemoryRules.select(current, participantId)),
      markRevealed: participantId =>
        store.update(groupId, current =>
          current.selectedParticipantId === participantId
            ? DeviceMemoryRules.markRevealed(current, participantId)
            : current,
        ),
      forget: () => store.update(groupId, DeviceMemoryRules.forget),
    }),
    [memory, store, groupId],
  );
}
